// ─── Stale-chunk recovery (shared) ───────────────────────────────────────────
// After a deploy, the production alias serves only the latest build's hashed
// chunks (Zones-DK91nXEc.js). A tab opened before the deploy still holds an
// index.html referencing the OLD chunk hashes, which now 404 — so navigating to
// a not-yet-loaded route throws when its lazy import() can't be fetched. The
// cure is a single hard reload to pull a fresh chunk manifest.
//
// Two independent signals can surface the failure; both funnel through
// reloadForStaleChunk():
//   1. Vite's `vite:preloadError` window event (the common path).
//   2. The root ErrorBoundary, when the failed import reaches React directly
//      (Safari doesn't always dispatch the event), or as the collateral
//      "undefined module" throw after we preventDefault the event below.
// A sessionStorage timestamp throttles the reload so a genuine (non-deploy)
// failure can't loop — if a chunk still fails right after a reload, we stand
// down and let the real crash screen show.
//
// Lives in its own module (it used to be inline in main.tsx) because the
// per-screen boundary in App.tsx also needs isStaleChunkRecovery(): a lazy
// route's import() rejects INSIDE that boundary, and it has to hand the error
// up to the root rather than show a screen-level card for it.
const RELOAD_TS = 'parkify_ops_chunk_reload_ts'
const RELOAD_THROTTLE_MS = 10_000

// Set the instant we kick off a recovery reload. The root boundary reads it to
// show a calm "updating" spinner instead of the alarming crash card while the
// reload is in flight. Needed because preventDefault'ing vite:preloadError
// resolves the import to `undefined`, so the throw React then sees is a generic
// "reading 'default'" error — message-sniffing alone wouldn't catch that case.
let staleChunkReloadPending = false

// Chunk-load failure messages vary by engine:
//   Chrome:  "Failed to fetch dynamically imported module: <url>"
//   Firefox: "error loading dynamically imported module: <url>"
//   Safari:  "Importing a module script failed."
const CHUNK_ERROR_RE = /dynamically imported module|Importing a module script failed/i
export const isChunkLoadError = (err: unknown): boolean =>
  err instanceof Error && CHUNK_ERROR_RE.test(err.message)

const recentlyReloaded = (): boolean =>
  Date.now() - Number(sessionStorage.getItem(RELOAD_TS) || 0) < RELOAD_THROTTLE_MS

// Kick off one throttled hard reload. Returns true if a reload was started,
// false if we just reloaded (caller should let the error surface instead).
export function reloadForStaleChunk(): boolean {
  if (recentlyReloaded()) return false
  staleChunkReloadPending = true
  sessionStorage.setItem(RELOAD_TS, String(Date.now()))
  window.location.reload()
  return true
}

// Is this caught error one a reload will cure?
//   • staleChunkReloadPending — the vite:preloadError listener already
//     started a reload; this throw is collateral, navigation is imminent.
//   • a fresh chunk error we haven't just reloaded for.
// A chunk error that persists right after a reload (recentlyReloaded) is a
// real failure, not a stale deploy → false, so the real crash card shows.
//
// Must be evaluated when the error is first caught, before
// reloadForStaleChunk() stamps the throttle — afterwards the same error reads
// as "recently reloaded". The root fallback captures it on its first render.
export const isStaleChunkRecovery = (err: unknown): boolean =>
  staleChunkReloadPending || (isChunkLoadError(err) && !recentlyReloaded())

// ─── vite:preloadError listener ──────────────────────────────────────────────
// preventDefault stops Vite from rethrowing; without a reload that suppression
// would resolve the import to `undefined` and trip the ErrorBoundary, so the
// two must go together. If we just reloaded (throttled, reloadForStaleChunk
// returns false) we DON'T preventDefault — the error surfaces to the boundary,
// which shows the real crash screen rather than looping.
export function installPreloadErrorRecovery(): void {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadForStaleChunk()) event.preventDefault()
  })
}
